import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../utils/apiConfig';

/**
 * 커스텀 예시 CRUD 훅
 * - 사용자별 커스텀 예시 로드/추가/삭제
 * - 기본 예시와 커스텀 예시 병합
 */
const useCustomExamples = ({ auth, showToast }) => {
  const [customExamples, setCustomExamples] = useState(null); // null = 아직 로드 안됨

  // 커스텀 예시 로드 (페이지 로드 시 1회)
  const loadCustomExamples = useCallback(async () => {
    if (!auth.currentUser) return;
    
    try {
      const API_BASE_URL = getApiBaseUrl();
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/user/custom-examples`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomExamples(data.custom_examples || {});
      } else {
        setCustomExamples({});
      }
    } catch (err) {
      console.error('커스텀 예시 로드 실패:', err);
      setCustomExamples({});
    }
  }, [auth.currentUser]);

  // 페이지 로드 시 커스텀 예시 로드 (한 번만)
  useEffect(() => {
    if (customExamples === null && auth.currentUser) {
      loadCustomExamples();
    }
  }, [customExamples, loadCustomExamples, auth.currentUser]);

  // 커스텀 예시 추가
  const saveCustomExample = async (category, subcategory, text) => {
    if (!text.trim()) return false;
    
    try {
      const API_BASE_URL = getApiBaseUrl();
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/user/custom-examples`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          category, 
          subcategory, 
          text, 
          type: 'positive'
        })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      await loadCustomExamples();
      showToast('예시를 추가했어요', 'success');
      return true;
    } catch (err) {
      console.error('커스텀 예시 저장 실패:', err);
      showToast('예시를 추가할 수 없었어요. 다시 시도해주세요.', 'error');
      return false;
    }
  };

  // 커스텀 예시 삭제
  const deleteCustomExample = async (category, subcategory, text) => {
    try {
      const API_BASE_URL = getApiBaseUrl();
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/user/custom-examples`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ category, subcategory, text, type: 'positive' })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      await loadCustomExamples();
      showToast('예시를 삭제했어요', 'success');
    } catch (err) {
      console.error('커스텀 예시 삭제 실패:', err);
      showToast('예시를 삭제할 수 없었어요. 다시 시도해주세요.', 'error');
    }
  };

  /**
   * 기본 예시 + 커스텀 예시 병합
   * @param {Object} categoryGroup - { examples: [...] }
   * @param {string} subcategory - 하위 카테고리 (행동발달용)
   * @param {string} selectedCategory - 현재 선택된 카테고리
   * @param {string} selectedSubject - 교과 과목
   */
  const getExamplesWithCustom = (categoryGroup, subcategory, selectedCategory, selectedSubject) => {
    const baseExamples = categoryGroup?.examples || [];
    
    if (!customExamples) return baseExamples;
    
    let customs = [];
    if (selectedCategory === '행동발달' && subcategory) {
      customs = customExamples['행동발달']?.[subcategory] || [];
    } else if (selectedCategory === '창체') {
      customs = (customExamples['창체'] || []).map(text => ({ text, isCustom: true }));
    } else if (selectedCategory === '교과' && selectedSubject) {
      customs = (customExamples['교과']?.[selectedSubject] || []).map(text => ({ text, isCustom: true }));
    }
    
    return [...baseExamples, ...customs];
  };

  return {
    customExamples,
    saveCustomExample,
    deleteCustomExample,
    getExamplesWithCustom,
    loadCustomExamples
  };
};

export default useCustomExamples;
